import { Button } from '@cian/ui-kit';
import { IconCommunicationPhone16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';

import { trackBlockedEarlyAccessPhoneClick, trackShowEarlyAccessPhoneClick } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../packages/EarlyAccessPaywall';
import { ESubscriptionLimitType } from '../../../packages/EarlyAccessPaywall/shared/types/context';
import * as phoneStyles from '../../components/Phone/Phone.css';
import { trackCall } from '../../components/Phone/tracking';
import { EDeviceType } from '../../types/device';
import { IPageState } from '../../types/page';
import { IFavoriteEntity } from '../../types/results';
import { hideLastDigits, parsePhoneNumber } from '../../utils/strings';

interface IPhoneEarlyAccessProps {
  deviceType: EDeviceType;
  favoriteEntity: IFavoriteEntity;
  page: IPageState;
  total: number;
  position: number;
  phone?: string | null;
}

enum EPhoneState {
  Button = 'button',
  Text = 'text',
}

const HOVER_TEXT = 'Показать телефон';
const COUNT_HIDE_SYMBOL = 2;

export const PhoneEarlyAccess: React.FC<IPhoneEarlyAccessProps> = ({
  deviceType,
  favoriteEntity,
  page,
  total,
  position,
  phone,
}) => {
  const earlyAccessContext = useEarlyAccessPaywallContext();
  const parsedPhone = phone ? parsePhoneNumber(phone) : '';
  const [stateType, setStateType] = React.useState<EPhoneState>(EPhoneState.Button);
  const [buttonText, setButtonText] = React.useState(() => hideLastDigits(parsedPhone, COUNT_HIDE_SYMBOL));
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setButtonText(hideLastDigits(parsedPhone, COUNT_HIDE_SYMBOL));
  }, [parsedPhone]);

  const handlePhoneClick = React.useCallback(async () => {
    if (!parsedPhone) {
      return;
    }

    const { checkContactsAccess } = earlyAccessContext;

    setIsLoading(true);
    const { isConfirmed, trackingStatus } = await checkContactsAccess({
      offerId: favoriteEntity.entityId,
      limitType: ESubscriptionLimitType.PhoneNumber,
    });
    setIsLoading(false);

    if (!isConfirmed) {
      if (trackingStatus) {
        trackBlockedEarlyAccessPhoneClick({
          entity: favoriteEntity,
          position,
          total,
          deviceType: page.deviceType,
          blockType: trackingStatus,
        });
      }

      return;
    }

    trackShowEarlyAccessPhoneClick({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
    if (deviceType === EDeviceType.Desktop) {
      setStateType(EPhoneState.Text);
    } else {
      trackCall({ entity: favoriteEntity, position, total, deviceType: page.deviceType });
      window.location.href = `tel:${parsedPhone.startsWith('+') ? parsedPhone : `+${parsedPhone}`}`;
    }
  }, [deviceType, earlyAccessContext, favoriteEntity, page.deviceType, parsedPhone, position, total]);

  const handleMouseOver = () => setButtonText(HOVER_TEXT);
  const handleMouseOut = () => setButtonText(hideLastDigits(parsedPhone, COUNT_HIDE_SYMBOL));

  if (!parsedPhone) {
    return null;
  }

  if (deviceType === EDeviceType.Desktop && stateType === EPhoneState.Text) {
    return (
      <span className={phoneStyles['phone']} data-testid="Phone">
        {parsedPhone}
      </span>
    );
  }

  if (deviceType === EDeviceType.Desktop) {
    return (
      <div className={phoneStyles['container']}>
        <Button
          size="XS"
          beforeIcon={<IconCommunicationPhone16 color="icon-inverted-default" />}
          fullWidth={true}
          onClick={handlePhoneClick}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </div>
    );
  }

  return (
    <Button fullWidth onClick={handlePhoneClick}>
      Позвонить
    </Button>
  );
};
